import axios from "axios";
import { useCallback } from "react";
import { getAuthToken } from "../context/AuthContext";

const baseUrl = process.env.REACT_APP_BACKEND_URL + "api/contacts";

const api = axios.create({
  baseURL: baseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const useContacts = () => {
  /**
   * Submit a contact form (public endpoint)
   * @param {string} companyId - The company ID
   * @param {Object} contactData - The contact form data
   * @returns {Promise<Object>} - Response data
   */
  const submitContactForm = useCallback(async (companyId, contactData) => {
    try {
      const response = await api.post(`/${companyId}`, contactData);
      return response.data;
    } catch (error) {
      console.error("Error submitting contact form:", error);
      throw error;
    }
  }, []);

  /**
   * Get all contacts for the current company
   * @param {Object} filters - Optional filters (status, read)
   * @returns {Promise<Array>} - List of contacts
   */
  const getCompanyContacts = useCallback(async (filters = {}) => {
    try {
      // Build query params
      const params = new URLSearchParams();
      if (filters.status) {
        params.append("status", filters.status);
      }
      if (filters.read !== undefined) {
        params.append("read", filters.read === true ? "1" : "0");
      }

      const query = params.toString() ? `?${params.toString()}` : "";
      const response = await api.get(`/${query}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching contacts:", error);
      throw error;
    }
  }, []);

  /**
   * Get a specific contact by ID
   * @param {string} contactId - The contact ID
   * @returns {Promise<Object>} - Contact details
   */
  const getContactById = useCallback(async (contactId) => {
    try {
      const response = await api.get(`/${contactId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching contact ${contactId}:`, error);
      throw error;
    }
  }, []);

  /**
   * Update a contact's status (read/unread, archive/inbox)
   * @param {string} contactId - The contact ID
   * @param {Object} updates - Properties to update
   * @returns {Promise<Object>} - Updated contact
   */
  const updateContact = useCallback(async (contactId, updates) => {
    try {
      const response = await api.patch(`/${contactId}`, updates);
      return response.data;
    } catch (error) {
      console.error(`Error updating contact ${contactId}:`, error);
      throw error;
    }
  }, []);

  /**
   * Update multiple contacts at once
   * @param {Array<string>} contactIds - Array of contact IDs
   * @param {Object} updates - Properties to update
   * @returns {Promise<Object>} - Result with updatedCount
   */
  const updateMultipleContacts = useCallback(async (contactIds, updates) => {
    try {
      const response = await api.patch("/", { contactIds, updates });
      return response.data;
    } catch (error) {
      console.error("Error updating multiple contacts:", error);
      throw error;
    }
  }, []);

  /**
   * Delete a contact
   * @param {string} contactId - The contact ID to delete
   * @returns {Promise<void>}
   */
  const deleteContact = useCallback(async (contactId) => {
    try {
      await api.delete(`/${contactId}`);
      return true;
    } catch (error) {
      console.error(`Error deleting contact ${contactId}:`, error);
      throw error;
    }
  }, []);

  return {
    submitContactForm,
    getCompanyContacts,
    getContactById,
    updateContact,
    updateMultipleContacts,
    deleteContact,
  };
};

export default useContacts;
